import { useCallback, useState } from "react";
import { type Api, type Me, previewUrlFor } from "../api";
import { ChatRail } from "./ChatRail";
import { MigratePanel } from "./MigratePanel";
import { SiteEditor } from "./SiteEditor";

/**
 * UX-2/UX-3 — the Website view: the site itself is the screen. A live
 * preview pane (the {slug}-preview host reflects overlay edits on the
 * next request) sits beside the chat rail; the visual editor is one click
 * away, and a workspace with no site gets the create-your-website flow
 * instead of dead cards.
 */
export function WebsiteView({
  api,
  me,
  canGenerate,
  editorRefreshKey,
  onImportComplete,
}: {
  api: Api;
  me: Me;
  canGenerate: boolean;
  editorRefreshKey: number;
  onImportComplete: () => void;
}) {
  const [previewNonce, setPreviewNonce] = useState(0);
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [showEditor, setShowEditor] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const reloadPreview = useCallback(() => setPreviewNonce((n) => n + 1), []);

  if (!me.hostname) {
    return (
      <div className="website-view">
        <section className="first-run">
          <div className="first-run-copy">
            <h1>Let's get your website up</h1>
            <p className="muted">
              Bring an existing site in, or describe your business and
              Groundbeam builds one for you. Either way you'll see it here and
              can change anything by chatting.
            </p>
          </div>
          <MigratePanel
            api={api}
            canGenerate={canGenerate}
            hostname={null}
            onImportComplete={onImportComplete}
          />
        </section>
      </div>
    );
  }

  const previewSrc = `${previewUrlFor(me.hostname)}${
    previewNonce ? `?gb-preview=${previewNonce}` : ""
  }`;

  return (
    <div className="website-view">
      <div className="website-toolbar">
        <div className="website-toolbar-left">
          <span className="site-status live">
            <span aria-hidden="true" className="status-dot" />
            Live
          </span>
          <a
            className="site-link"
            href={`https://${me.hostname}`}
            target="_blank"
            rel="noreferrer"
          >
            {me.hostname}
          </a>
        </div>
        <div className="website-toolbar-right">
          <fieldset className="device-toggle" aria-label="Preview width">
            <button
              type="button"
              className={device === "desktop" ? "active" : ""}
              onClick={() => setDevice("desktop")}
            >
              Desktop
            </button>
            <button
              type="button"
              className={device === "mobile" ? "active" : ""}
              onClick={() => setDevice("mobile")}
            >
              Mobile
            </button>
          </fieldset>
          <button type="button" onClick={reloadPreview}>
            Refresh
          </button>
          <button
            type="button"
            className={showEditor ? "active" : ""}
            onClick={() => setShowEditor((v) => !v)}
          >
            {showEditor ? "Close editor" : "Visual editor"}
          </button>
          <button
            type="button"
            className={showImport ? "active" : ""}
            onClick={() => setShowImport((v) => !v)}
          >
            {showImport ? "Close import" : "Re-import / generate"}
          </button>
        </div>
      </div>

      {showImport && (
        <MigratePanel
          api={api}
          canGenerate={canGenerate}
          hostname={me.hostname}
          onImportComplete={() => {
            onImportComplete();
            reloadPreview();
          }}
        />
      )}

      {showEditor ? (
        <SiteEditor api={api} hostname={me.hostname} key={editorRefreshKey} />
      ) : (
        <div className="website-split">
          <div className={`preview-pane ${device}`}>
            <iframe
              key={previewNonce}
              className="preview-frame"
              src={previewSrc}
              title="Website preview"
            />
          </div>
          <ChatRail
            api={api}
            onApplied={reloadPreview}
            onOpenEditor={() => setShowEditor(true)}
          />
        </div>
      )}
    </div>
  );
}
